import qrOrderClient, { QR_BASE_URL } from "./qrOrderClient";

// QR오더(테이블 주문/직원호출/리뷰) 고객용 API 모음.
// 백엔드가 admin 모듈에서 front 모듈로 이관되어, 경로에서 기존 "/admin" 접두어를 제거했고
// 호출 대상도 front(8080)로 바뀌었다. 회원 로그인은 front 가 이미 갖고 있는 /login 을 재사용한다.

function authHeader(memberToken) {
  return memberToken ? { headers: { Authorization: `Bearer ${memberToken}` } } : {};
}

// ===================== 테이블오더(메뉴/주문) =====================

/** langCode: 다국어 메뉴판 언어코드 (EN/JA/ZH), 생략하거나 KO면 한국어 원문 */
export async function getTableOrderMenu(token, langCode) {
  const res = await qrOrderClient.get("/table-order/public/menu", { params: { token, langCode: langCode || undefined } });
  return res.data;
}

export async function placeTableOrder(token, memo, items, memberToken, split) {
  const res = await qrOrderClient.post(
    "/table-order/public/order",
    { memo, items, ...(split || {}) },
    { params: { token }, ...authHeader(memberToken) }
  );
  return res.data;
}

// ===================== 실시간 공유 장바구니 (같은 테이블 여러 기기 동기화) =====================
// 서버(Redis)가 테이블 단위 장바구니를 보관하고, 변경 시 SSE(cart-changed)로 브로드캐스트한다.
// 모든 함수는 변경 후의 장바구니 스냅샷(라인 배열)을 data 로 돌려준다.

export async function getSharedCart(token) {
  const res = await qrOrderClient.get("/table-order/public/cart", { params: { token } });
  return res.data;
}

export async function addSharedCartItem(token, productId, optionId, qty) {
  const res = await qrOrderClient.post("/table-order/public/cart/items", { productId, optionId, qty }, { params: { token } });
  return res.data;
}

export async function setSharedCartItemQty(token, optionId, qty) {
  const res = await qrOrderClient.put(`/table-order/public/cart/items/${optionId}`, { qty }, { params: { token } });
  return res.data;
}

export async function removeSharedCartItem(token, optionId) {
  const res = await qrOrderClient.delete(`/table-order/public/cart/items/${optionId}`, { params: { token } });
  return res.data;
}

export async function getActiveTableOrders(token) {
  const res = await qrOrderClient.get("/table-order/public/active", { params: { token } });
  return res.data;
}

/** 고객이 자기 주문을 직접 취소한다 (조리 시작 전(RECEIVED)에만 가능 - 서버에서 최종 검증) */
export async function cancelTableOrder(token, tableOrderId) {
  const res = await qrOrderClient.put(`/table-order/public/order/${tableOrderId}/cancel`, null, { params: { token } });
  return res.data;
}

/**
 * 고객이 자기 주문에 담긴 항목의 수량을 직접 바꾼다(조리 시작 전(RECEIVED)에만 가능 - 서버에서
 * 최종 검증). qty=0 이면 그 항목을 아예 빼고, 마지막 남은 항목이면 주문 전체가 취소된다.
 */
export async function updateTableOrderItemQty(token, tableOrderId, tableOrderItemId, qty) {
  const res = await qrOrderClient.patch(
    `/table-order/public/order/${tableOrderId}/items/${tableOrderItemId}`,
    { qty },
    { params: { token } }
  );
  return res.data;
}

/**
 * 고객이 자기 주문에 담긴 항목의 옵션을 같은 상품의 다른 옵션으로 바꾼다(예: ICE→HOT).
 * 조리 시작 전(RECEIVED)에만 가능(서버에서 최종 검증). 수량/상품은 그대로, 옵션과 금액만 바뀐다.
 */
export async function updateTableOrderItemOption(token, tableOrderId, tableOrderItemId, optionId) {
  const res = await qrOrderClient.patch(
    `/table-order/public/order/${tableOrderId}/items/${tableOrderItemId}/option`,
    { optionId },
    { params: { token } }
  );
  return res.data;
}

/** 리뷰 작성 화면용 - 로그인한 회원이 이 테이블에서 오늘 결제완료한 주문 목록 */
export async function getReviewableTableOrders(token, memberToken) {
  const res = await qrOrderClient.get(
    "/table-order/public/reviewable-orders",
    { params: { token }, ...authHeader(memberToken) }
  );
  return res.data;
}

/**
 * 리뷰 유도 배너 문구 조회
 * 같은 안내를 리뷰 작성 영역 상단에 보여준다). 매장이 꺼두었거나 미설정이면 enabled=false.
 */
export async function getReviewIncentive(token) {
  const res = await qrOrderClient.get("/table-order/public/review-incentive", { params: { token } });
  return res.data;
}

/** 주문상태/메뉴 품절/직원호출 상태 실시간 반영용 SSE 스트림 URL. */
export function tableOrderStreamUrl(qrToken) {
  return `${QR_BASE_URL}/table-order/public/stream?token=${encodeURIComponent(qrToken)}`;
}

// ===================== QR오더 전용 회원 로그인 (선택 사항) =====================
// front 가 이미 갖고 있는 회원 로그인(/login, ROLE_MEMBER JWT 발급)을 그대로 재사용한다.
// 응답 형태는 ApiResponse<{ accessToken, refreshToken, memberName }> 로 동일하다.

export async function loginQrOrderMember(memberAccount, memberPassword) {
  const res = await qrOrderClient.post("/login", { memberAccount, memberPassword });
  return res.data;
}

// ===================== 직원호출(콜벨) =====================

export async function requestTableCall(token, callType = "STAFF", memo) {
  const res = await qrOrderClient.post("/table-call/public/call", { callType, memo }, { params: { token } });
  return res.data;
}

export async function getPendingTableCalls(token) {
  const res = await qrOrderClient.get("/table-call/public/pending", { params: { token } });
  return res.data;
}

// ===================== 리뷰 작성 (결제완료 주문, 로그인한 회원만) =====================

export async function createProductReview(memberToken, tableOrderItemId, rating, content) {
  const res = await qrOrderClient.post(
    "/review/public/product",
    { tableOrderItemId, rating, content },
    authHeader(memberToken)
  );
  return res.data;
}

export async function createStoreReview(memberToken, tableOrderId, rating, content) {
  const res = await qrOrderClient.post(
    "/review/public/store",
    { tableOrderId, rating, content },
    authHeader(memberToken)
  );
  return res.data;
}

// ===================== 상품/옵션 이미지 URL (front 8080이 서빙) =====================
// 이미지 서빙도 front 로 이관되어, QR오더 화면 이미지는 admin(8082)이 아니라 front(8080)에서 받는다.
// (front-web 쇼핑몰 페이지의 api/product.js 는 여전히 admin 을 쓰므로 그쪽과 별개로 여기 둔다.)

export function qrThumbnailImageUrl(productId) {
  return `${QR_BASE_URL}/product/${productId}/image/thumbnail`;
}

export function qrMainImageUrl(productId) {
  return `${QR_BASE_URL}/product/${productId}/image/main`;
}

export function qrOptionImageUrl(optionId) {
  return `${QR_BASE_URL}/product/option/${optionId}/image`;
}

// ===================== 사이드 메뉴 제안 (업셀) =====================
// 레퍼런스 "사이드 메뉴 제안으로 객단가가 상승해요" 벤치마킹 - 상품을 장바구니에 담을 때
// 관리자가 등록해둔 사이드 메뉴를 "이 메뉴와 잘 어울려요" 팝업으로 제안한다.
// 응답은 메뉴 목록과 동일한 상품 형태(옵션 포함)라 addToCart/isProductSoldOut 등 기존 헬퍼를 그대로 재사용할 수 있다.

export async function getSideRecommends(productId) {
  const res = await qrOrderClient.get(`/product/${productId}/side-recommend`);
  return res.data;
}

// ===================== 브랜드 소개 =====================
// 매장이 속한 프랜차이즈 브랜드의 소개(로고/소개HTML/대표이미지/링크)를 조회한다.
// 매장에 브랜드가 없거나 미사용이면 data=null → 화면에서 브랜드소개 섹션을 감춘다.
// 브랜드 이미지는 admin(9082)이 서빙하므로 상품이미지(front 8080)와 달리 ADMIN_BASE_URL 을 쓴다.

const ADMIN_BASE_URL = process.env.REACT_APP_ADMIN_BASE_URL || "http://localhost:9082";

export async function getBrandInfo(token) {
  const res = await qrOrderClient.get("/table-order/public/brand", { params: { token } });
  return res.data;
}

export function brandLogoUrl(brandId) {
  return `${ADMIN_BASE_URL}/admin/brand/${brandId}/logo`;
}

export function brandMainUrl(brandId) {
  return `${ADMIN_BASE_URL}/admin/brand/${brandId}/main`;
}

// ===================== 테이블오더 결제 (NICEPAY 구모듈 V1) =====================
// 선불(PREPAID) 테이블오더의 실제 결제. 서버가 결제 활성화 여부/JS SDK URL 을 내려주고(config),
// 결제 준비(prepare)로 결제 건(단건/분할)을 만든 뒤, 결제 건별 티켓(ticket: MID/Moid/Amt/EdiDate/
// SignData/ReturnURL 등)을 받아 pg-web SDK 의 goPay(form) 으로 결제창을 띄운다. 인증 성공 시 NICEPAY 가
// ReturnURL(서버)로 POST → 서버가 NextAppURL 승인 처리 후 SPA 결과페이지로 redirect 한다.

export async function getTablePaymentConfig() {
  const res = await qrOrderClient.get("/table-order/public/pay/config");
  return res.data; // ApiResponse<{ enabled, jsSdkUrl }>
}

// splits: 비우면 전액 단건, [{payerSeq,payerLabel,amount}...] 주면 분할결제(1N/상품별/금액별).
export async function prepareTablePayment(token, tableOrderId, method, splits) {
  const res = await qrOrderClient.post(
    "/table-order/public/pay/prepare",
    { tableOrderId, method, splits },
    { params: { token } }
  );
  return res.data; // ApiResponse<{ tableOrderId, totalAmount, remainingAmount, jsSdkUrl, payments:[...] }>
}

export async function getTablePaymentStatus(token, tableOrderId) {
  const res = await qrOrderClient.get("/table-order/public/pay/status", { params: { token, tableOrderId } });
  return res.data; // ApiResponse<{ tableOrderId, totalAmount, paidAmount, paymentStatus, payments:[...] }>
}

// 결제 취소(환불) - 주문의 승인완료 결제 건들을 나이스페이 취소 API로 전액 환불한다.
export async function cancelTablePayment(token, tableOrderId, reason) {
  const res = await qrOrderClient.post(
    "/table-order/public/pay/cancel",
    { tableOrderId, reason, actor: "STAFF" },
    { params: { token } }
  );
  return res.data; // ApiResponse<{ tableOrderId, cancelledCount, paymentStatus }>
}

// READY 결제건 하나에 대해 결제창 폼 필드(EdiDate/SignData 포함)를 발급받는다.
export async function issueTablePaymentTicket(token, pgOrderId) {
  const res = await qrOrderClient.post(
    "/table-order/public/pay/ticket",
    { pgOrderId },
    { params: { token } }
  );
  return res.data; // ApiResponse<{ mid, moid, amt, goodsName, ediDate, signData, returnUrl, payMethod }>
}

// NICEPAY 구모듈 pg-web JS SDK 를 한 번만 동적 로드한다. (goPay 전역 함수 제공)
let nicepaySdkPromise = null;
export function loadNicepaySdk(jsSdkUrl) {
  if (typeof window !== "undefined" && typeof window.goPay === "function") return Promise.resolve();
  if (nicepaySdkPromise) return nicepaySdkPromise;
  nicepaySdkPromise = new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = jsSdkUrl;
    s.onload = () => resolve();
    s.onerror = () => { nicepaySdkPromise = null; reject(new Error("결제 모듈(NICEPAY)을 불러오지 못했습니다.")); };
    document.head.appendChild(s);
  });
  return nicepaySdkPromise;
}

// 티켓으로 결제창을 띄운다. 구모듈은 hidden input 을 가진 form 을 만들어 goPay(form) 을 호출한다.
// 인증 완료 시 결제창이 폼에 인증결과를 append 한 뒤 부모 페이지의 nicepaySubmit() 콜백을 호출하므로,
// nicepaySubmit 에서 그 폼을 ReturnURL(form.action)로 submit 해야 서버 콜백이 실행된다.
// (nicepayClose 는 사용자가 결제창을 닫았을 때 호출됨 - 미정의 시 ReferenceError 방지용으로 함께 정의)
export function openNicepayPay(ticket) {
  if (typeof window === "undefined" || typeof window.goPay !== "function") {
    throw new Error("결제 모듈이 준비되지 않았습니다. 잠시 후 다시 시도해주세요.");
  }
  const fields = {
    PayMethod: ticket.payMethod || "CARD",
    GoodsName: ticket.goodsName,
    Amt: String(ticket.amt),
    MID: ticket.mid,
    Moid: ticket.moid,
    EdiDate: ticket.ediDate,
    SignData: ticket.signData,
    ReturnURL: ticket.returnUrl,
    CharSet: "utf-8",
    NpLang: "KO",
    GoodsCl: "1",
    TransType: "0",
  };
  const form = document.createElement("form");
  form.name = "nicepayForm";
  form.method = "post";
  // 나이스페이 구모듈은 결제요청 폼을 EUC-KR 로 보내야 결제창 헤더의 한글(GoodsName)이 안 깨진다.
  // (CharSet=utf-8 필드는 "응답(ReturnURL) 인코딩" 설정이지 요청 인코딩이 아니다 - 매뉴얼 샘플도 accept-charset=euc-kr)
  form.acceptCharset = "euc-kr";
  form.action = ticket.returnUrl; // 인증결과 append 후 nicepaySubmit() 이 이 action(ReturnURL)으로 제출
  form.style.display = "none";
  Object.entries(fields).forEach(([k, v]) => {
    if (v === undefined || v === null) return;
    const input = document.createElement("input");
    input.type = "hidden";
    input.name = k;
    input.value = v;
    form.appendChild(input);
  });
  document.body.appendChild(form);

  // 나이스페이 구모듈 결제창 콜백. 인증 성공 시 결제창이 form 에 인증결과를 붙이고 nicepaySubmit() 을 호출한다.
  window.nicepaySubmit = function nicepaySubmit() {
    form.submit();
  };
  // 결제창 종료(취소/닫기) 시 호출. 특별히 할 일은 없지만 미정의 시 ReferenceError 가 나므로 정의해 둔다.
  window.nicepayClose = function nicepayClose() {
    // no-op: 사용자가 결제를 취소/닫음. 주문은 PENDING 으로 남아 재시도 가능.
  };

  window.goPay(form);
}
